import React, { useEffect, useState } from "react";

import { Link, useNavigate } from "react-router-dom";

import { Cloud, Download, Info, Lock, Mail } from "lucide-react";

import { useFeature } from "../hooks";
import { useTypedSelector } from "../store";

// Static pages for the BASYS demo (flag `demo_pages`, ROC-118), built from the v9
// screens s-flow, s-commitments and s-proposal. Sample content only.

const btn =
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border px-4 py-2 text-sm font-medium leading-5 transition-colors disabled:cursor-not-allowed disabled:opacity-50";
const btnPrimary = `${btn} border-transparent bg-primary-500 text-white hover:bg-primary-600`;
const btnSecondary = `${btn} border-gray-300 bg-white text-primary-700 hover:bg-gray-50`;
const card = "rounded-lg border border-gray-200 bg-white";

const TenantLogo: React.FC<{ className: string }> = ({ className }) => {
	const tenant = useTypedSelector((state) => state.tenant.tenant);
	return tenant?.logo_base64 ? (
		<img src={tenant.logo_base64} alt={tenant.name} className={className} />
	) : (
		<span className="text-2xl font-semibold text-gray-900">
			{tenant?.name ?? "Basys"}
		</span>
	);
};

// v9 shows flow and commitments without the app chrome: logo, then the page header.
const BarePage: React.FC<{
	title: string;
	subtitle: string;
	action: React.ReactNode;
	children: React.ReactNode;
}> = ({ title, subtitle, action, children }) => {
	// Layout sets the theme on other pages; a bare page opened first must set it too.
	const basysTheme = useFeature("basys_theme");
	useEffect(() => {
		if (basysTheme) document.documentElement.dataset.theme = "basys";
	}, [basysTheme]);
	return (
		<div className="min-h-screen bg-gray-50 text-sm text-gray-900">
			<div className="mx-auto w-full max-w-[1440px] px-4 min-[900px]:px-8">
				<div className="pt-6">
					<TenantLogo className="-ml-2 h-16" />
				</div>
				<PageHead title={title} subtitle={subtitle} actions={action} />
				<div className="pb-16">{children}</div>
			</div>
		</div>
	);
};

const PageHead: React.FC<{
	title: string;
	subtitle: string;
	actions: React.ReactNode;
	breadcrumb?: React.ReactNode;
}> = ({ title, subtitle, actions, breadcrumb }) => (
	<div className="flex flex-col items-start justify-between gap-6 py-6 min-[900px]:flex-row min-[900px]:items-center">
		<div>
			{breadcrumb && (
				<div className="mb-1.5 text-xs text-gray-500">{breadcrumb}</div>
			)}
			<h1 className="text-xl font-normal leading-normal text-gray-900">
				{title}
			</h1>
			<p className="mt-1 max-w-[72ch] text-base leading-normal text-gray-500">
				{subtitle}
			</p>
		</div>
		<div className="flex shrink-0 flex-wrap items-center gap-3">{actions}</div>
	</div>
);

const FLOW_STEPS = [
	{
		title: "A statement reaches the inbox",
		who: "Merchant or rep",
		text: "Email, text photo, PDF, XLSX, CSV, MSG or JPEG. Any email to the inbox starts the workflow.",
		screen: "Connect mailbox",
		note: "One-time setup, then automatic",
		path: "/settings",
	},
	{
		title: "Three months run in parallel",
		who: "Automatic",
		text: "Each month is read, classified and recalculated. Unreadable details stop for a person.",
		screen: "Analysis",
		note: "Side-by-side fees, padding, flags",
		path: "/",
	},
	{
		title: "The pricing team reviews the quote",
		who: "Pricing team",
		text: "The first reviewer applies a template and edits rates inside floors and ceilings.",
		screen: "Customize Quote",
		note: "Rates, limits, restricted profit",
		path: "/config",
	},
	{
		title: "The next approver gets an email",
		who: "Pricing team or manager",
		text: "Anything outside the limits waits for approval before the proposal can leave.",
		screen: "Approval queue",
		note: "Email link, decision, audit trail",
		path: "/approvals",
	},
	{
		title: "The rep gets the proposal",
		who: "Basys rep",
		text: "The approved one-page PDF arrives by email, ready to share on the merchant call.",
		screen: "PDF proposal",
		note: "Merchant savings only",
		path: "/proposal",
	},
	{
		title: "Salesforce is updated",
		who: "Automatic",
		text: "Mapped standard or custom fields receive the rates, the savings and the PDF.",
		screen: "Salesforce connector",
		note: "Fields mapped automatically from your schema",
		path: "/settings",
	},
];

// .flow: 6 columns, 3 under 1100px, 1 under 900px, with borders between the cells.
const flowStep =
	"flex min-h-[280px] flex-col gap-3 border-b border-gray-200 p-6 hover:bg-gray-50 min-[900px]:border-r min-[900px]:[&:nth-child(3n)]:border-r-0 min-[1100px]:border-b-0 min-[1100px]:[&:nth-child(3n)]:border-r min-[1100px]:last:border-r-0";

export const DemoFlowPage: React.FC = () => {
	const navigate = useNavigate();
	return (
		<BarePage
			title="How a merchant gets priced"
			subtitle="From the statement arriving in the inbox to the proposal in Salesforce. Click a step to open its screen."
			action={
				<button
					type="button"
					className={btnPrimary}
					onClick={() => navigate("/")}
				>
					Open Statements
				</button>
			}
		>
			<div className="grid grid-cols-1 overflow-hidden rounded-lg border border-gray-200 bg-white min-[900px]:grid-cols-3 min-[1100px]:grid-cols-6">
				{FLOW_STEPS.map((step, i) => (
					<Link key={step.title} to={step.path} className={flowStep}>
						<span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-500">
							{i + 1}
						</span>
						<h3 className="text-base font-medium leading-[1.35] text-gray-900">
							{step.title}
						</h3>
						<div className="text-xs font-medium text-primary-700">
							{step.who}
						</div>
						<p className="text-sm leading-normal text-gray-500">{step.text}</p>
						<div className="mt-auto border-t border-gray-100 pt-3 text-xs text-gray-500">
							<b className="mb-0.5 block text-[13px] font-medium text-gray-900">
								{step.screen}
							</b>
							{step.note}
							<br />
							<span className="font-medium text-primary-700">
								Open screen →
							</span>
						</div>
					</Link>
				))}
			</div>
		</BarePage>
	);
};

type Dot = "ok" | "warn";
const DOT: Record<Dot, string> = { ok: "bg-green-500", warn: "bg-sand-300" };

const Hist: React.FC<{
	items: { text: string; small?: string; dot?: Dot }[];
}> = ({ items }) => (
	<ul>
		{items.map((item) => (
			<li
				key={item.text}
				className="flex items-start gap-3 border-t border-gray-100 py-2.5 text-sm first:border-t-0 first:pt-0"
			>
				<span
					className={`mt-[7px] h-2 w-2 flex-none rounded-full ${DOT[item.dot ?? "ok"]}`}
				/>
				<div className="min-w-0 flex-1">
					{item.text}
					{item.small && (
						<small className="block text-xs text-gray-500">{item.small}</small>
					)}
				</div>
			</li>
		))}
	</ul>
);

const Card: React.FC<{
	title: string;
	sub?: string;
	bodyClass?: string;
	children: React.ReactNode;
}> = ({ title, sub, bodyClass = "", children }) => (
	<div className={card}>
		<div className="px-6 pt-6">
			<div className="text-lg font-normal leading-normal text-gray-900">
				{title}
			</div>
			{sub && <div className="mt-0.5 text-sm text-gray-500">{sub}</div>}
		</div>
		<div className={`p-6 ${bodyClass}`}>{children}</div>
	</div>
);

const Metric: React.FC<{
	k: string;
	v: string;
	n: React.ReactNode;
	good?: boolean;
}> = ({ k, v, n, good }) => (
	<div
		className={`rounded-lg border p-4 ${good ? "border-green-100 bg-green-50" : "border-gray-200 bg-white"}`}
	>
		<div className="text-xs text-gray-500">{k}</div>
		<div
			className={`mt-1 text-2xl font-semibold leading-[1.2] tabular-nums ${good ? "text-green-800" : "text-gray-900"}`}
		>
			{v}
		</div>
		<div className="mt-1 text-xs text-gray-500">{n}</div>
	</div>
);

const AGREED = [
	"Statements arrive by email, and photos sent by text. PDF, XLSX, CSV, MSG or JPEG. IC+, tiered and flat rate.",
	"350 to 400 custom pricing templates across partners and direct merchants.",
	"Side-by-side comparison and first pricing in the first meeting. 1 to 3 days becomes under 10 minutes, with 5 minutes as the aim.",
	"Missing pages or unreadable details are flagged for a person to confirm.",
	"Floors and ceilings for reps. Pricing team or manager approval on custom discounts.",
	"Padding in the merchant's current rates is highlighted.",
	"Rate tables kept current, with audit logs.",
	"Full Basys branding, with our branding removable.",
	"Salesforce integration, including custom objects.",
].map((text) => ({ text }));

const SECURITY = [
	{ text: "SOC 2 Type 2" },
	{ text: "US data centers" },
	{ text: "Model APIs that do not train on your data" },
	{ text: "Data retention set per contract. Default one month." },
	{
		text: "Accuracy",
		small: "Placeholder. The number will be confirmed.",
		dot: "warn" as const,
	},
];

const BUILD = [
	{
		title: "Platform features",
		text: "Floors and ceilings on Customize Quote. The approval queue with email. Roles with profit visibility. Templates at Basys scale with import and an API. Three months in parallel. Analytics for managers.",
	},
	{
		title: "Basys setup",
		text: "basys.rocketfee.com with the Basys logo and colors. Your templates imported. Your roles, limits and approval routing. Retention per contract.",
	},
	{
		title: "Connections",
		text: "One Microsoft 365 inbox, or forwarding to statements@basys.rocketfee.com. Salesforce with your custom objects, auto-mapped from your schema.",
	},
];

export const DemoCommitmentsPage: React.FC = () => {
	const navigate = useNavigate();
	return (
		<BarePage
			title="What we deliver for Basys"
			subtitle="The commitments from Monday's call, the speed target, how we build it, and how your data is handled."
			action={
				<button
					type="button"
					className={btnPrimary}
					onClick={() => navigate("/flow")}
				>
					See the workflow
				</button>
			}
		>
			<div className="space-y-4">
				<div className="grid grid-cols-1 gap-6 min-[900px]:grid-cols-2">
					<Card title="What we agreed on Monday">
						<Hist items={AGREED} />
					</Card>
					<div className="space-y-4">
						<Card title="Speed">
							<div className="grid grid-cols-1 gap-4 min-[900px]:grid-cols-2">
								<Metric
									good
									k="Target per merchant"
									v="Under 15 min"
									n="three months of statements"
								/>
								<Metric
									k="Aiming for"
									v="Under 10 min"
									n="first pricing on the first call"
								/>
							</div>
						</Card>
						<Card title="Security and data">
							<Hist items={SECURITY} />
						</Card>
					</div>
				</div>
				<Card
					title="How we build it"
					sub="Every feature is part of the platform. Only the setup is Basys's own."
					bodyClass="grid grid-cols-1 gap-4 min-[900px]:grid-cols-3"
				>
					{BUILD.map((b) => (
						<div key={b.title}>
							<div className="text-base font-medium text-gray-900">
								{b.title}
							</div>
							<p className="mt-2 text-gray-500">{b.text}</p>
						</div>
					))}
				</Card>
			</div>
		</BarePage>
	);
};

export const PROPOSAL_STAGES = ["Draft", "In review", "Approved", "Released"];
const CURRENT_STAGE = 1;
const APPROVER = "Dana Whitfield";

const FEE_LINES = [
	["Interchange (pass-through, unchanged)", "$3,260.00 → $3,260.00"],
	["Dues, fees & assessments", "$259.48 → $239.84"],
	["Authorization & discount fees", "$1,372.53 → $552.84"],
	["Non-qualified downgrades", "$333.75 → $0.00"],
	["Other fees", "$97.65 → $28.00"],
];

// .pvsteps li (done / on / next) and its .pvn number.
const stageClass = (i: number): [string, string] => {
	if (i < CURRENT_STAGE)
		return [
			"border-gray-200 bg-gray-50 text-gray-700",
			"border-green-500 bg-green-500 text-gray-900",
		];
	if (i === CURRENT_STAGE)
		return [
			"border-primary-400 bg-primary-50 font-semibold text-gray-900",
			"border-primary-500 bg-primary-500 text-white",
		];
	return [
		"border-gray-200 bg-gray-50 text-gray-500",
		"border-gray-300 bg-white text-gray-500",
	];
};

const pvLabel =
	"mb-1.5 text-xs font-semibold uppercase tracking-[.04em] text-gray-500";
const pvTextarea =
	"block w-full resize-y rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm leading-normal text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500";

export const DemoProposalPage: React.FC = () => {
	const [cover, setCover] = useState(
		"Thank you for the time on our call. This proposal compares your current processing fees with Basys pricing, based on your last three monthly statements.",
	);
	const [terms, setTerms] = useState(
		"Pricing is valid for 30 days from the release date. Interchange and card brand fees pass through at cost and can change when the card brands change them. Final pricing depends on the signed merchant agreement.",
	);

	return (
		<div className="mx-auto w-full max-w-[1440px] px-8 pb-16 text-sm text-gray-900">
			<PageHead
				breadcrumb={
					<>
						<Link to="/">Statements</Link> /{" "}
						<Link to="/">Cedar &amp; Finch Market</Link> / Proposal
					</>
				}
				title="Merchant proposal"
				subtitle="One page for the merchant call. Merchant numbers only, co-branded with the partner when the partner has co-branding on."
				actions={
					<>
						<span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-sand-300 bg-sand-100 px-2 py-1 text-xs font-medium leading-[1.3] text-sand-900">
							<span className="h-1.5 w-1.5 rounded-full bg-current" />
							Awaiting approval · {APPROVER}
						</span>
						<button
							type="button"
							className={btnSecondary}
							disabled
							title="Available after approval"
						>
							<Download className="h-4 w-4" />
							Download PDF
						</button>
						<button
							type="button"
							className={btnPrimary}
							disabled
							title="Available after approval"
						>
							<Mail className="h-4 w-4" />
							Email to rep
						</button>
					</>
				}
			/>

			<div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-6 py-4">
				<div className="flex items-center gap-4">
					<span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-primary-100 text-primary-500">
						<Cloud className="h-5 w-5" />
					</span>
					<div>
						<div className="text-base font-medium text-gray-900">
							Posts to Salesforce automatically when approved
						</div>
						<div className="text-sm text-gray-500">
							Nothing is written to the opportunity until {APPROVER} approves
							the quote.
						</div>
					</div>
				</div>
			</div>

			<div className={`${card} mx-auto mb-4 max-w-[880px] px-5 py-4`}>
				<div className="flex items-center gap-2 text-[15px] font-semibold text-gray-900">
					Proposal RF-1048 · Version 1
				</div>
				<ol
					className="mt-2.5 flex flex-wrap gap-1.5"
					aria-label="Proposal status"
				>
					{PROPOSAL_STAGES.map((stage, i) => {
						const [li, n] = stageClass(i);
						return (
							<li
								key={stage}
								aria-current={i === CURRENT_STAGE ? "step" : undefined}
								className={`flex items-center gap-1.5 rounded-full border py-1 pl-1 pr-2.5 text-[13px] ${li}`}
							>
								<span
									className={`grid h-5 w-5 place-items-center rounded-full border text-[11px] font-semibold ${n}`}
								>
									{i < CURRENT_STAGE ? "✓" : i + 1}
								</span>
								{stage}
							</li>
						);
					})}
				</ol>
				<p className="mt-3 text-sm text-gray-700">
					Waiting for {APPROVER}. You can edit the cover note and the terms. You
					cannot release the proposal before approval.
				</p>
			</div>

			<div className={`${card} mx-auto max-w-[880px] overflow-hidden`}>
				<div className="flex flex-col items-start justify-between gap-4 border-b border-gray-200 px-8 py-7 min-[720px]:flex-row">
					<div>
						<div className="text-xs font-semibold uppercase tracking-[.06em] text-primary-700">
							Merchant savings proposal
						</div>
						<h2 className="mt-1.5 text-2xl font-semibold text-gray-900">
							Cedar &amp; Finch Market
						</h2>
						<p className="mt-2 text-gray-500">
							Prepared 29 Oct 2026 from three monthly statements · Riley Park,
							Basys · for Prairie State Bank
						</p>
					</div>
					<div className="flex items-center gap-4">
						<span className="leading-[1.2]">
							<span className="whitespace-nowrap text-[15px] font-semibold text-gray-900">
								Prairie State Bank
							</span>
							<br />
							<span className="whitespace-nowrap text-[11px] text-gray-500">
								Merchant services
							</span>
						</span>
						<span className="h-11 w-px bg-gray-200" />
						<TenantLogo className="-my-1.5 h-[60px] w-auto" />
					</div>
				</div>

				<div className="px-8 py-7">
					<label className="mb-5 block">
						<div className={pvLabel}>Cover note</div>
						<textarea
							className={pvTextarea}
							rows={3}
							value={cover}
							onChange={(e) => setCover(e.target.value)}
						/>
					</label>

					<div className="grid grid-cols-1 gap-4 min-[720px]:grid-cols-3">
						<Metric
							k="Current monthly fees"
							v="$5,323.41"
							n="effective rate 2.89%"
						/>
						<Metric
							k="Proposed monthly fees"
							v="$4,080.68"
							n={
								<>
									effective rate 2.21%{" "}
									<span className="font-medium text-green-700">
										· 68 bps lower
									</span>
								</>
							}
						/>
						<Metric
							good
							k="Estimated annual savings"
							v="$14,912.76"
							n="$1,242.73 a month · 23.3% lower"
						/>
					</div>

					<div className="mt-8 text-base font-medium text-gray-900">
						Average monthly fees, side by side
						<span className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-gray-500">
							<Lock className="h-[13px] w-[13px]" />
							Numbers change only through pricing
						</span>
					</div>
					<div className="mt-3">
						{FEE_LINES.map(([label, value]) => (
							<div
								key={label}
								className="flex justify-between border-t border-gray-100 py-2.5 text-sm first:border-t-0"
							>
								<span>{label}</span>
								<b className="font-medium tabular-nums">{value}</b>
							</div>
						))}
						<div className="flex justify-between border-t border-gray-100 py-2.5 text-sm font-semibold">
							<span>Total</span>
							<b className="font-semibold tabular-nums">
								$5,323.41 → $4,080.68
							</b>
						</div>
					</div>

					<div className="mt-6 flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
						<Info className="mt-px h-5 w-5 flex-none text-gray-500" />
						<div>
							Interchange-Plus pricing passes card brand costs through at cost.
							Basys adds a transparent markup of 0.20% + $0.10 per transaction.
							Results depend on card mix and volume.
						</div>
					</div>

					<label className="mt-6 block">
						<div className={pvLabel}>Terms</div>
						<textarea
							className={pvTextarea}
							rows={3}
							value={terms}
							onChange={(e) => setTerms(e.target.value)}
						/>
					</label>
				</div>

				<div className="flex flex-col justify-between gap-1 border-t border-gray-200 bg-gray-50 px-8 py-3 text-xs text-gray-500 min-[720px]:flex-row">
					<span>
						Prairie State Bank with Basys · basys.rocketfee.com · Proposal
						RF-1048
					</span>
					<span>Draft · awaiting approval</span>
				</div>
			</div>
		</div>
	);
};
